/* Created by Interactive 63 - https://interactive63.com */
/**
 * Track sampling: the value of an animated property at a point in time. The editor uses this to
 * show values at the playhead; it follows the same hold and easing rules as the exported CSS.
 */
import type { ElementId, Project, PropertyKey, PropertyValue, Track } from '../model/types';
import { PROPERTIES } from '../properties/registry';
import { interpolateValue, sampleKeyframeList } from './interpolate';
import { effectiveKeyframes } from './repeat';

export { interpolateValue };

/** Applies a property's output limits, for properties browsers clamp such as opacity. */
function clampOutput(property: PropertyKey, value: PropertyValue): PropertyValue {
  const definition = PROPERTIES[property];
  if (!definition.clampOutput || typeof value !== 'number') return value;
  return Math.min(definition.max ?? Infinity, Math.max(definition.min ?? -Infinity, value));
}

/**
 * Samples a track at an iteration-local time in 0..duration. Before the first keyframe and after
 * the last the value holds. A repeating track is sampled across its expanded cycles. Returns
 * undefined for a track with no keyframes.
 */
export function sampleTrack(
  track: Track,
  time: number,
  duration: number,
): PropertyValue | undefined {
  const { keyframes } = effectiveKeyframes(track, duration);
  const value = sampleKeyframeList(PROPERTIES[track.property].valueType, keyframes, time);
  return value === undefined ? undefined : clampOutput(track.property, value);
}

/** Samples every enabled track on an element, keyed by property. */
export function sampleElement(
  project: Project,
  elementId: ElementId,
  time: number,
): Partial<Record<PropertyKey, PropertyValue>> {
  const values: Partial<Record<PropertyKey, PropertyValue>> = {};
  for (const track of project.tracks) {
    if (track.elementId !== elementId || !track.enabled) continue;
    const value = sampleTrack(track, time, project.scene.duration);
    if (value !== undefined) values[track.property] = value;
  }
  return values;
}
